'use strict';

/**
 * Releases -- the graphical deploy surface. One read (GET /1.0/Releases via providers.DeployAPI) backs the
 * whole page: what commit runs on dev and prod, the As-Builts grouped by where they are in the pipeline
 * (built, on dev awaiting validation, validated and ready, promoted), and the recent deploy requests.
 *
 * The buttons never deploy anything; they QUEUE a DeployRequest that a deploy node claims and runs (the deploy
 * credentials live on that node, never in the browser). Deploy dev queues a DeployDev; Promote to prod queues a
 * Promote, and is offered only when every As-Built on dev has been validated. Validate reuses the app's
 * validateAsBuilt flow. Template-driven per the Pict conventions; no innerHTML.
 *
 * An environment already carrying a deploy takes its button away rather than leaving one that would 409. The
 * server refuses either way (SingleFlightLanes is the shared rule both sides read), but a button you can press
 * that will not work is the thing that let a promote get queued twice: it looked available, so it got clicked.
 * In its place goes what is actually in flight and a way to go look at it.
 *
 * Not every deliverable rides this pipeline. Work landed in a sibling workspace (the node runner, the deploy
 * tool) has a commit no app ref can ever contain, so a dev deploy could never pick it up and it sat at Built
 * forever, re-tested and skipped on every deploy. Those close as Delivered and get their own group here rather
 * than a permanent seat in the pending bucket. See the !AsBuilt block in data_model/Plansheet.ddl.
 */

const libPictView = require('pict-view');
const libLanes = require('../SingleFlightLanes.js');
// The mark and tooltip furniture shared with the board and every entity list, so a row's "WI-080" and its
// hover card can never drift from what the rest of the app shows for the same Work Item (WI #452).
const libMarkup = require('./Markup-Templates.js');

// The workspace this page's pipeline ships. The server now says which one (GET /1.0/Releases carries
// DeployableWorkspace, read from the tenant's Workspace marked Deployable); this literal survives only as the
// fallback for a plan sheet that has not declared one, which is what it always effectively was.
const _DEPLOYABLE_WORKSPACE_FALLBACK = 'plansheet';
// Statuses that mean "still riding the deploy pipeline". For a row belonging to another workspace one of
// these is a leftover from a node that stamped it before Delivered existed, not a real position.
const _PIPELINE_STATUSES = { Built: true, Deployed: true, Validated: true };

class PictViewReleases extends libPictView
{
	_data() { return this.pict.AppData.Releases || {}; }
	_api() { return this.pict.providers.DeployAPI; }

	_ms(pValue)
	{
		if (!pValue) { return 0; }
		let tmpString = String(pValue);
		let tmpParsed = Date.parse(tmpString);
		if (!isNaN(tmpParsed)) { return tmpParsed; }
		tmpParsed = Date.parse(tmpString.replace(' ', 'T') + 'Z');
		return isNaN(tmpParsed) ? 0 : tmpParsed;
	}

	_ago(pValue)
	{
		let tmpMs = this._ms(pValue);
		if (!tmpMs) { return ''; }
		let tmpSeconds = Math.floor((Date.now() - tmpMs) / 1000);
		if (tmpSeconds < 0) { tmpSeconds = 0; }
		if (tmpSeconds < 60) { return tmpSeconds + 's ago'; }
		let tmpMinutes = Math.floor(tmpSeconds / 60);
		if (tmpMinutes < 60) { return tmpMinutes + 'm ago'; }
		let tmpHours = Math.floor(tmpMinutes / 60);
		if (tmpHours < 24) { return tmpHours + 'h ago'; }
		return Math.floor(tmpHours / 24) + 'd ago';
	}

	// A compact span ("45s", "2m", "1h 3m"), for elapsed and for how long one took. Distinct from _ago, which
	// is a point in the past. Same shape the Runs page uses, so the two deploy surfaces read alike.
	_dur(pMs)
	{
		let tmpSeconds = Math.max(0, Math.floor((Number(pMs) || 0) / 1000));
		if (tmpSeconds < 60) { return tmpSeconds + 's'; }
		let tmpMinutes = Math.floor(tmpSeconds / 60);
		if (tmpMinutes < 60) { return tmpMinutes + 'm'; }
		let tmpHours = Math.floor(tmpMinutes / 60);
		let tmpRemMinutes = tmpMinutes % 60;
		return tmpRemMinutes ? (tmpHours + 'h ' + tmpRemMinutes + 'm') : (tmpHours + 'h');
	}

	// The stage and the deploy log are text a NODE wrote, and these templates interpolate raw. A log line
	// reading "<button>Promote</button>" would render as one, on the page whose whole job is to tell a person
	// the truth about production before they press something. Escape on the way in, exactly as the Runs page
	// does with its step logs.
	_escape(pValue)
	{
		return String(pValue == null ? '' : pValue)
			.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
	}

	_short(pValue) { return String(pValue || '').slice(0, 12); }

	/**
	 * The row's Work Item, when the As-Built names one: the mark data "WI-080" renders from, plus a stash
	 * of what the hover tooltip needs once the row is actually on screen (see _attachWorkItemTooltips).
	 * Before this the row showed the As-Built's OWN number and no link at all -- so validating meant going
	 * to the Work Items page and hunting for the number by the string next to it (WI #452). Returns null
	 * for an As-Built that predates the IDWorkItem link or whose Work Item is gone, and the row falls back
	 * to showing its own number, exactly as it always has.
	 */
	_workItemLink(pRow)
	{
		let tmpIDWorkItem = Number(pRow.IDWorkItem) || 0;
		let tmpNumber = Number(pRow.WorkItemNumber) || 0;
		if (!tmpIDWorkItem || !tmpNumber) { return null; }
		let tmpMarkup = this.pict.providers.Markup;
		let tmpDesignator = tmpMarkup ? tmpMarkup.designator('WorkItem', tmpNumber) : ('WI-' + tmpNumber);
		this._workItemTooltipData[tmpIDWorkItem] = {
			Number: tmpNumber, Title: pRow.WorkItemTitle || '', Type: pRow.WorkItemType || '',
			Priority: pRow.WorkItemPriority || '', Status: pRow.WorkItemStatus || '',
			Description: pRow.WorkItemDescription || ''
		};
		return { IDWorkItem: tmpIDWorkItem, MarkSlot: [{ Designator: tmpDesignator, KindClass: 'ps-mark-workitem' }] };
	}

	_statusClass(pStatus)
	{
		let tmpMap = { Built: 'built', Deployed: 'deployed', Validated: 'validated', Promoted: 'promoted', Superseded: 'muted',
			Delivered: 'delivered',
			Requested: 'deployed', Running: 'running', Succeeded: 'validated', Failed: 'failed', Cancelled: 'muted' };
		return tmpMap[pStatus] || 'muted';
	}

	// The live one-liner on a deploy request: what the node last got through and how long it has been at it,
	// or how long it took once it is done. A 0-or-1 slot so a request that has done nothing stays quiet.
	//
	// The stage is the last step that COMPLETED, which is what the node's log can actually prove. It reads
	// "database backup done - running 4m" rather than naming a step as in progress, because the step now
	// running is the one nothing has reported yet, and inventing its name is how a page starts lying about a
	// deploy (Feature #124).
	_requestTiming(pRow)
	{
		let tmpStage = this._escape(pRow.Stage || '');
		if (pRow.Status === 'Running')
		{
			let tmpStartedMs = this._ms(pRow.StartedDate);
			let tmpElapsed = tmpStartedMs ? ('running ' + this._dur(Date.now() - tmpStartedMs)) : 'running';
			if (!tmpStage) { return [{ Timing: tmpElapsed }]; }
			// A step that FAILED is the last thing that happened, not progress: say so, since a deploy sitting
			// on a failed step for minutes is the case where a person most needs to stop watching and look.
			let tmpWord = pRow.StageFailed ? ' FAILED - ' : ((Number(pRow.StepsDone) > 0) ? ' done - ' : ' - ');
			return [{ Timing: tmpStage + tmpWord + tmpElapsed }];
		}
		if (Number(pRow.DurationMs) > 0) { return [{ Timing: 'took ' + this._dur(pRow.DurationMs) }]; }
		return [];
	}

	// A node's heartbeat state as a badge class. Stale and Never are both "nothing is coming", so they read
	// the same; Suspended is deliberate and reads muted rather than alarming.
	_nodeStateClass(pState)
	{
		return ({ Live: 'validated', Stale: 'failed', Never: 'failed', Suspended: 'muted', None: 'failed' })[pState] || 'muted';
	}

	// Does this As-Built ride the deploy pipeline this page is about? An empty WorkspaceKey predates the
	// field and is read as the deployable workspace, which is what every such record actually was.
	// The key the server reported for this tenant, or the historical literal when it reported none.
	_deployableWorkspace() { return String(this._data().DeployableWorkspace || "") || _DEPLOYABLE_WORKSPACE_FALLBACK; }
	_isDeployable(pRow) { return !pRow.WorkspaceKey || String(pRow.WorkspaceKey) === this._deployableWorkspace(); }

	onBeforeRender(pRenderable)
	{
		let tmpD = this._data();
		let tmpDevTarget = 'dev';
		let tmpProdTarget = 'prod';
		let tmpAsBuilts = tmpD.AsBuilts || [];

		// What runs where: one card per environment, from EnvironmentState (the DB source of truth).
		let tmpEnvByName = {};
		(tmpD.Environments || []).forEach((pE) => { if (pE && pE.Environment) { tmpEnvByName[pE.Environment] = pE; } });
		tmpD.EnvCards = [tmpDevTarget, tmpProdTarget].map((pEnv) =>
		{
			let tmpRow = tmpEnvByName[pEnv] || {};
			return {
				Env: pEnv,
				Label: (pEnv === tmpProdTarget) ? 'Production' : 'Dev sandbox',
				Commit: tmpRow.CommitRef ? this._short(tmpRow.CommitRef) : '(nothing deployed)',
				Ago: tmpRow.DeployedDate ? this._ago(tmpRow.DeployedDate) : ''
			};
		});

		// Rebuilt every render, keyed by IDWorkItem, for _attachWorkItemTooltips to read once the rows it
		// describes are actually on screen (see _workItemLink).
		this._workItemTooltipData = {};

		// The As-Builts, grouped by where they sit in the pipeline. A row carries only what its template shows.
		let fRow = (pRow) =>
		{
			let tmpWI = this._workItemLink(pRow);
			return {
				IDAsBuilt: pRow.IDAsBuilt, Number: pRow.Number || pRow.AsBuiltNumber || pRow.IDAsBuilt,
				Title: pRow.Title || '', Status: pRow.Status || '', StatusClass: this._statusClass(pRow.Status),
				Environment: pRow.Environment || '', Commit: this._short(pRow.CommitHash),
				Workspace: pRow.WorkspaceKey || '',
				// 0-or-1 slots: a row either links to the Work Item it closed out, or falls back to the
				// As-Built's own number, never both.
				NumLinkSlot: tmpWI ? [tmpWI] : [],
				NumPlainSlot: tmpWI ? [] : [{ Number: pRow.Number || pRow.AsBuiltNumber || pRow.IDAsBuilt }]
			};
		};
		// The Built bucket carries two things the others do not: why a row is stuck, when the server could infer
		// it, and the manual way out. Both are 0-or-1-element slots so the row template renders them only when
		// they apply, the same way LoadErrorSlot does below. A person without content.approve sees the
		// explanation but no button, because the endpoint would refuse them anyway.
		let tmpCanApprove = !!tmpD.CanApprove;
		let fBuiltRow = (pRow) =>
		{
			let tmpBuilt = fRow(pRow);
			// The sentence comes from the SERVER now, because the server is the only side holding the evidence
			// for it -- the node's own account of which rows it examined and left behind (WI #368). This page
			// used to compose the sentence itself, from two timestamps, and it named a cause it had not
			// checked: on 2026-08-18 it told an operator that four As-Builts had never landed while their
			// commit was running in production. An older payload with no note falls back to what this page can
			// see for itself, which is that dev moved on and this did not, and stops there.
			tmpBuilt.StrandedSlot = pRow.Stranded
				? [{ Note: this._escape(pRow.StrandedNote
					|| ('Dev deployed ' + this._ago((tmpEnvByName[tmpDevTarget] || {}).DeployedDate)
						+ ' and this is still Built. Nothing here can say why.')) }]
				: [];
			// Rebind is offered only where the server has already proved it will work, and where it is offered
			// Supersede is taken AWAY (WI #369). A row whose commit is provably live is not a candidate for
			// retirement: superseding it would record that shipped, running work had been abandoned. On
			// 2026-08-18 four rows in exactly this state had Supersede as the only control on the page, and
			// the operator had to be talked out of the one button the software offered.
			tmpBuilt.RebindSlot = (tmpCanApprove && pRow.CanRebind) ? [{ IDAsBuilt: pRow.IDAsBuilt }] : [];
			tmpBuilt.SupersedeSlot = (tmpCanApprove && !pRow.CanRebind) ? [{ IDAsBuilt: pRow.IDAsBuilt }] : [];
			return tmpBuilt;
		};
		// "Built, not yet on dev" means WAITING FOR A DEPLOY, so only the workspace this pipeline deploys
		// belongs in it. A sibling-workspace record put there is a promise the deploy can never keep.
		tmpD.BuiltRows = tmpAsBuilts.filter((pRow) => pRow.Status === 'Built' && this._isDeployable(pRow)).map(fBuiltRow);
		tmpD.PendingRows = tmpAsBuilts.filter((pRow) => pRow.Status === 'Deployed').map(fRow);
		tmpD.ValidatedRows = tmpAsBuilts.filter((pRow) => pRow.Status === 'Validated').map(fRow);
		tmpD.PromotedRows = tmpAsBuilts.filter((pRow) => pRow.Status === 'Promoted').map(fRow).slice(0, 30);
		// Delivered elsewhere: shipped work whose commit is in another repository. Its own group, because
		// it is finished, not pending -- and it says which workspace, so the row explains itself.
		//
		// A sibling-workspace row still sitting at Built belongs here too, carrying its real status. An
		// older node stamped it Built before Delivered existed, and taking it out of the pending bucket
		// without putting it anywhere would make a deliverable vanish from the page entirely, which is the
		// same class of silence this work item is about. dev/backfill-asbuilt-workspace.js corrects the
		// status; until it runs, the row is at least visible and honest about what it says.
		tmpD.DeliveredRows = tmpAsBuilts
			.filter((pRow) => pRow.Status === 'Delivered' || (!this._isDeployable(pRow) && !!_PIPELINE_STATUSES[pRow.Status]))
			.map(fRow).slice(0, 30);
		tmpD.BuiltEmpty = tmpD.BuiltRows.length ? [] : [1];
		tmpD.PendingEmpty = tmpD.PendingRows.length ? [] : [1];
		tmpD.ValidatedEmpty = tmpD.ValidatedRows.length ? [] : [1];
		tmpD.PromotedEmpty = tmpD.PromotedRows.length ? [] : [1];
		// No empty-state line for Delivered: on a single-repo project it is always empty, and a permanent
		// "nothing here" under a heading nobody needed is worse than the heading not being there.
		tmpD.DeliveredSlot = tmpD.DeliveredRows.length ? [1] : [];

		// Node liveness (WI #296). The page used to infer it from the queue, which cannot be done: a request
		// sitting Requested is a healthy node between polls and a node that died an hour ago, drawn the same
		// way. The heartbeat answers it directly, so the strip reports what the nodes did and the queue is left
		// to report on itself. A tenant with no registered node gets the one sentence that matters instead of
		// an empty list that looks like everything is fine.
		let tmpFleet = tmpD.DeployFleet || null;
		tmpD.NodeRows = (tmpD.Nodes || []).map((pNode) => ({
			Host: pNode.Host || '',
			Roles: (pNode.Roles && pNode.Roles.length) ? pNode.Roles.join(', ') : 'no roles published',
			State: pNode.State || '',
			StateClass: this._nodeStateClass(pNode.State),
			// "polled 2m ago" for a node with a heartbeat, and the state word for one that has none, so a
			// column that is blank for a node that never started never reads as a node that is fine.
			Seen: pNode.SeenWords ? ('polled ' + pNode.SeenWords) : 'never polled'
		}));
		tmpD.FleetSlot = tmpFleet
			? [{ Note: tmpFleet.Note || '', State: tmpFleet.State || '', StateClass: this._nodeStateClass(tmpFleet.State) }]
			: [];
		let tmpFleetLive = !!tmpFleet && (tmpFleet.State === 'Live');
		// Only worth saying on a request nothing has claimed, and only when the fleet cannot claim it. On a
		// live fleet a queued request is simply waiting for the next poll, and saying so on every row would
		// train a person to read the note as decoration.
		let tmpQueuedNote = (tmpFleet && !tmpFleetLive) ? String(tmpFleet.Note || '') : '';

		// What each environment already has in flight, off the deploy request feed this page has already loaded.
		// No extra read: the rows are right here, and the rule for reading them is the same one the server
		// enforces on the way in.
		let tmpRequests = tmpD.DeployRequests || [];
		// A lane can be held by a deploy request OR by a run: pressing Promote queues a run of the
		// promote-prod toolchain, which never touches the request feed. Reading only the requests is
		// what let a queued promotion sit in the lane while this page went on offering the button --
		// the stale button WI #314 removed. Either kind of row holds the lane, so both are consulted,
		// and the request keeps precedence so the older surface's wording is unchanged where it applies.
		let tmpOpenRuns = tmpD.OpenRuns || [];
		let tmpProdInFlight = libLanes.findOpenDeployIn(tmpRequests, 'deploy:' + tmpProdTarget)
			|| libLanes.findOpenRunIn(tmpOpenRuns, 'toolchain:promote-prod');
		let tmpDevInFlight = libLanes.findOpenDeployIn(tmpRequests, 'deploy:' + tmpDevTarget)
			|| libLanes.findOpenRunIn(tmpOpenRuns, 'toolchain:deploy-dev');

		// Promote gating. The audited path is a fully-validated dev batch. But As-Builts are the audit trail,
		// not a hard gate: a real change can sit on dev with no As-Built at all (a hand deploy, or a commit with
		// no work item), and locking production out of that is the software demanding data it does not need. So a
		// promote is ALSO offered whenever dev is simply running a commit production is not -- it ships dev's
		// commit as-is. Only "dev and prod already match" truly has nothing to promote. See WI #330.
		let tmpDevCommit = String((tmpEnvByName[tmpDevTarget] || {}).CommitRef || '');
		let tmpProdCommit = String((tmpEnvByName[tmpProdTarget] || {}).CommitRef || '');
		let tmpDevBatch = tmpAsBuilts.filter((pRow) => pRow.Environment === tmpDevTarget && (pRow.Status === 'Deployed' || pRow.Status === 'Validated'));
		let tmpUnvalidated = tmpDevBatch.filter((pRow) => pRow.Status !== 'Validated');
		let tmpValidatedReady = (tmpDevBatch.length > 0 && tmpUnvalidated.length === 0);
		// Dev is running a commit production does not have (ahead or diverged) -- there is something to ship.
		let tmpDevAhead = !!tmpDevCommit && (tmpDevCommit !== tmpProdCommit);
		let tmpCanPromote = (tmpValidatedReady || tmpDevAhead);

		// An in-flight promotion outranks both. Ready and Blocked are both about whether there is anything to
		// ship; this is about the lane, and while the lane is busy neither of those questions is the one to answer.
		tmpD.PromoteInFlight = tmpProdInFlight
			? [{
				// A run and a deploy request are described in different words. describeDeploy reads the
				// row's Action to decide whether it is a promotion, and a run has no Action -- so calling
				// it for a run announces a dev deploy holding the production lane.
				Note: tmpProdInFlight.IDDeployRequest
					? libLanes.describeDeploy(tmpProdInFlight)
					: libLanes.describeRun(tmpProdInFlight),
				ForceSlot: tmpCanApprove ? [1] : []
			}]
			: [];
		// Which surface holds the lane, so "Show it" lands somewhere that can actually show it. A run is
		// not in the deploy request feed, and scrolling someone to an empty table is a dead end.
		tmpD.PromoteInFlightIDRun = (tmpProdInFlight && !tmpProdInFlight.IDDeployRequest)
			? (Number(tmpProdInFlight.IDRun) || 0) : 0;
		tmpD.PromoteReady = (!tmpProdInFlight && tmpCanPromote) ? [1] : [];
		tmpD.PromoteBlocked = (!tmpProdInFlight && !tmpCanPromote) ? [1] : [];
		// "is queued (12s ago)" for one nothing has picked up yet, "is running (queued 3m ago)" for one a node
		// took. Saying "queued" twice in one sentence is what the naive version did.
		tmpD.PromoteNote = tmpProdInFlight
			? ('Promotion #' + (tmpProdInFlight.Number || tmpProdInFlight.DeployRequestNumber
					|| tmpProdInFlight.IDDeployRequest || tmpProdInFlight.IDRun)
				+ ' is ' + libLanes.statusWords(tmpProdInFlight.Status)
				+ ' (' + ((tmpProdInFlight.Status === 'Requested') ? '' : 'queued ')
				+ this._ago(tmpProdInFlight.CreateDate || tmpProdInFlight.StartedDate) + ').')
			: tmpValidatedReady
				? (tmpDevBatch.length + ' validated on dev, ready to promote to production.')
				: tmpDevAhead
					? ('Dev is on ' + this._short(tmpDevCommit) + ', production on ' + (tmpProdCommit ? this._short(tmpProdCommit) : 'nothing')
						+ ' -- ' + (tmpDevBatch.length === 0 ? 'no As-Builts' : (tmpUnvalidated.length + ' of ' + tmpDevBatch.length + ' not validated'))
						+ '. Promoting ships dev\'s commit as-is.')
					: 'Dev and production already match; nothing to promote.';

		// The header's Deploy dev button gets the same treatment for its own lane.
		tmpD.DeployDevReady = tmpDevInFlight ? [] : [1];
		tmpD.DeployDevInFlight = tmpDevInFlight
			? [{ Label: 'Dev deploy #' + (tmpDevInFlight.Number || tmpDevInFlight.DeployRequestNumber
					|| tmpDevInFlight.IDDeployRequest || tmpDevInFlight.IDRun)
				+ ' ' + libLanes.statusWords(tmpDevInFlight.Status) }]
			: [];

		// How long the last ones took, so a person knows what they are in for before they click and whether
		// the one they are watching is running long. Read off the most recent SUCCEEDED deploy of each action:
		// a failed one stopped early and would report a fast deploy that never happened.
		let fLastDuration = (pAction) =>
		{
			let tmpDone = tmpRequests.filter((pRow) => (pRow.Action === pAction) && (pRow.Status === 'Succeeded') && (Number(pRow.DurationMs) > 0))[0];
			return tmpDone ? (((pAction === 'Promote') ? 'last promote ' : 'last dev deploy ') + this._dur(tmpDone.DurationMs)) : '';
		};
		let tmpDurations = [fLastDuration('DeployDev'), fLastDuration('Promote')].filter((pText) => pText);
		tmpD.DurationsSlot = tmpDurations.length ? [{ Note: tmpDurations.join(', ') + '.' }] : [];

		// The deploy request feed: what has been queued, is running, or recently finished.
		tmpD.RequestRows = (tmpD.DeployRequests || []).map((pRow) => ({
			Number: pRow.Number || pRow.DeployRequestNumber || pRow.IDDeployRequest,
			Action: pRow.Action || '', Environment: pRow.Environment || '',
			Status: pRow.Status || '', StatusClass: this._statusClass(pRow.Status),
			Commit: this._short(pRow.CommitRef), Ago: this._ago(pRow.CreateDate || pRow.StartedDate),
			// Only an OPEN request can be closed by hand: a node that died mid-run leaves one Running with
			// nothing alive to finish it. A request that already succeeded or failed said what happened, and
			// rewriting that would erase the outcome instead of cleaning anything up. Kept as a per-row slot so
			// the feed stays in its original order rather than being split into two lists.
			CancelSlot: (tmpCanApprove && (pRow.Status === 'Requested' || pRow.Status === 'Running'))
				? [{ IDDeployRequest: pRow.IDDeployRequest }] : [],
			// Why a queued request is not moving, when the answer is about the node rather than the queue.
			StalledSlot: ((pRow.Status === 'Requested') && tmpQueuedNote) ? [{ Note: tmpQueuedNote }] : [],
			// The live line: what the deploy last got through and how long it has been going, or how long it
			// took once it is over. A 0-or-1 slot, so a queued request that has done nothing yet stays quiet.
			TimingSlot: this._requestTiming(pRow),
			// The log the node wrote. Offered only when there is one, so a button never opens an empty box.
			LogSlot: (pRow.ResultLog && String(pRow.ResultLog).trim()) ? [{ IDDeployRequest: pRow.IDDeployRequest }] : []
		})).slice(0, 25);
		tmpD.RequestsEmpty = tmpD.RequestRows.length ? [] : [1];

		tmpD.LoadErrorSlot = tmpD.LoadError ? [{ Message: tmpD.LoadError }] : [];

		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable, pAddress, pRecord, pContent)
	{
		if (this.pict.CSSMap) { this.pict.CSSMap.injectCSS(); }
		this._attachWorkItemTooltips();
		return super.onAfterRender(pRenderable, pAddress, pRecord, pContent);
	}

	/**
	 * The board's own hover card, anchored to every "WI-080" link this page just painted: same shape
	 * (a hand-built .ps-tip, matching Board's _tooltipHTML) so hovering a validation row shows exactly what
	 * hovering the card on the board would (WI #452).
	 *
	 * richTooltip, not Board's pinnableTooltip: pinnableTooltip listens for click ON THE ELEMENT IT IS
	 * GIVEN and calls preventDefault to pin/unpin, which on a real `<a href>` cancels the browser's own
	 * default action -- the navigation the second half of this work item asked for. Board never hits this
	 * because its card mark navigates through an onclick handler, not a real href, so there is nothing for
	 * preventDefault to cancel. This row's mark IS a real href on purpose (open in a new tab, or the
	 * current one, per WI #452), so it gets the plain hover-only tooltip instead.
	 *
	 * Rebuilt on every render rather than once: this page repaints its whole template on every refresh
	 * (including the 5s poll while a deploy is in flight -- see _ensurePolling), which replaces every row
	 * element, so a handle from the last paint is anchored to nothing. Destroying and re-collecting here is
	 * the same discipline Board's _attachCardTooltips uses for the same reason.
	 */
	_attachWorkItemTooltips()
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.richTooltip !== 'function' || typeof document === 'undefined') { return; }

		this._destroyWorkItemTooltips();
		this._tooltipHandles = [];
		let tmpData = this._workItemTooltipData || {};
		let tmpElements = document.querySelectorAll('.ps-rel .ps-rel-num-link[data-idworkitem]');
		for (let i = 0; i < tmpElements.length; i++)
		{
			let tmpElement = tmpElements[i];
			let tmpRecord = tmpData[tmpElement.getAttribute('data-idworkitem')];
			if (!tmpRecord) { continue; }
			this._tooltipHandles.push(tmpModal.richTooltip(tmpElement, this._workItemTooltipHTML(tmpRecord),
				{ position: 'right', delay: 320, maxWidth: '400px', interactive: true }));
		}
	}

	_destroyWorkItemTooltips()
	{
		(this._tooltipHandles || []).forEach((pHandle) =>
		{
			try { if (pHandle && typeof pHandle.destroy === 'function') { pHandle.destroy(); } }
			catch (pError) { /* the element is already gone; nothing to tidy */ }
		});
		this._tooltipHandles = [];
	}

	// Leaving Releases for another page has to take its tooltips with it, the same reason Board tears its
	// own down (see PictView-Board.js teardown): a tooltip anchors to a row element, and once another view
	// paints over the workspace that anchor is detached and can never fire the mouseleave that would have
	// closed it. Wired from _setActiveNav in Pict-Application-Plansheet.js.
	teardown() { this._destroyWorkItemTooltips(); }

	// Built from the same mark and cell shape the board's own card tooltip uses (Pict-Provider-Markup's
	// tooltipMark), so this can never drift into a second description of what a Work Item's hover card
	// looks like.
	_workItemTooltipHTML(pData)
	{
		let tmpMarkup = this.pict.providers.Markup;
		if (!tmpMarkup) { return ''; }
		let tmpMark = tmpMarkup.tooltipMark('WorkItem',
			{ Title: pData.Title, WorkItemNumber: pData.Number, Description: pData.Description },
			{ Cells: [{ K: 'Type', V: pData.Type }, { K: 'Pri', V: pData.Priority }, { K: 'Status', V: pData.Status }] });
		let tmpCells = (tmpMark.Cells || []).map((pCell) =>
			'<div class="ps-cell"><span class="ps-cell-k">' + this._escape(pCell.K) + '</span>'
			+ '<span class="ps-cell-v">' + this._escape(pCell.V) + '</span></div>').join('');
		let tmpDescription = String(tmpMark.Description || '').slice(0, 400);
		return '<div class="ps-tip">'
			+ '<div class="ps-mark ' + this._escape(tmpMark.KindClass) + '">'
			+ '<span class="ps-mark-d">' + this._escape(tmpMark.Designator) + '</span>'
			+ '<span class="ps-mark-t">' + this._escape(tmpMark.Title) + '</span></div>'
			+ '<div class="ps-cells">' + tmpCells + '</div>'
			+ (tmpDescription ? ('<p class="ps-tip-desc">' + this._escape(tmpDescription) + '</p>') : '')
			+ '</div>';
	}

	// Entry point from the router: load then paint.
	show() { return this.refresh(); }

	// "Show it" on an in-flight lane: scroll the deploy request feed into view, where the row that is
	// holding the lane sits with its Cancel control. A plain #anchor href would be read by the hash
	// router as a route change, so this scrolls rather than navigating.
	showRequests()
	{
		if (typeof document === 'undefined') { return; }
		// A run holds the lane: the deploy request feed below has nothing to show, so go to the Runs page
		// where the run and its controls actually are.
		if (Number(this._data().PromoteInFlightIDRun) > 0)
		{
			if (this.pict.PictApplication && typeof this.pict.PictApplication.showRuns === 'function')
			{
				return this.pict.PictApplication.showRuns();
			}
		}
		let tmpSection = document.getElementById('ps-rel-requests');
		if (tmpSection && typeof tmpSection.scrollIntoView === 'function')
		{
			tmpSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}

	// Open the log a deploy node wrote for one request. The whole log is already in the page's store (the
	// Releases read carries it), so this opens what is on hand rather than going back to the server for text
	// that is already here. Escaped, because it is node-written text going into a dialog.
	showLog(pIDDeployRequest)
	{
		let tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || typeof tmpModal.show !== 'function') { return; }
		let tmpRow = (this._data().DeployRequests || []).filter((pRow) => Number(pRow.IDDeployRequest) === Number(pIDDeployRequest))[0];
		if (!tmpRow) { return; }
		let tmpHeading = (tmpRow.Action || 'Deploy') + ' to ' + (tmpRow.Environment || '?')
			+ (tmpRow.CommitRef ? (' at ' + this._short(tmpRow.CommitRef)) : '')
			+ ((Number(tmpRow.DurationMs) > 0) ? (', took ' + this._dur(tmpRow.DurationMs)) : '');
		tmpModal.show(
		{
			title: 'Deploy #' + (tmpRow.Number || tmpRow.IDDeployRequest) + ' log',
			content: '<p style="font-size:12px;color:var(--theme-color-text-secondary,#5b6376);margin:0 0 8px;">'
				+ this._escape(tmpHeading) + '</p>'
				+ '<pre class="ps-rel-log">' + this._escape(tmpRow.ResultLog || '') + '</pre>',
			buttons: [{ Hash: 'close', Label: 'Close', Style: 'primary' }]
		});
	}

	refresh()
	{
		let tmpAPI = this._api();
		if (!tmpAPI || typeof tmpAPI.loadReleases !== 'function') { this.render(); this._ensurePolling(); return Promise.resolve(); }
		// A transient fetch failure must not blank the pane: the loader parks its own error on AppData, and
		// this catch covers anything the render itself throws.
		return tmpAPI.loadReleases().then(() => { this.render(); this._ensurePolling(); }).catch(() => this.render());
	}

	// Keep the page live while a deploy is actually moving, so stage and elapsed tick without a person sitting
	// on the Refresh button. Self-terminating in both directions: it never starts unless something is in
	// flight, and it stops the moment the queue settles or you navigate off Releases. Same pattern and cadence
	// as the Runs page. setInterval rather than requestAnimationFrame so a backgrounded tab keeps up.
	_onReleasesPage() { return (typeof window !== 'undefined' && window.location && /^#?\/?releases\b/.test(String(window.location.hash || ''))); }
	// Whether anything is still moving, which is what keeps the page polling. A run counts: promoting is
	// a run now, so watching only the request feed meant the page sat still while a promotion ran and
	// never noticed it finish.
	_hasOpenRequest()
	{
		let tmpData = this._data();
		let fOpen = (pRow) => (pRow.Status === 'Requested') || (pRow.Status === 'Running');
		return ((tmpData.DeployRequests) || []).some(fOpen) || ((tmpData.OpenRuns) || []).some(fOpen);
	}
	_ensurePolling()
	{
		if (typeof window === 'undefined' || this._pollTimer) { return; }
		if (!this._onReleasesPage() || !this._hasOpenRequest()) { return; }
		let tmpSelf = this;
		this._pollTimer = setInterval(function ()
		{
			if (!tmpSelf._onReleasesPage() || !tmpSelf._hasOpenRequest()) { tmpSelf._stopPolling(); return; }
			tmpSelf.refresh();
		}, 5000);
	}
	_stopPolling() { if (this._pollTimer) { clearInterval(this._pollTimer); this._pollTimer = null; } }
}

module.exports = PictViewReleases;

module.exports.default_configuration =
{
	ViewIdentifier: 'Plansheet-Releases',
	DefaultRenderable: 'Plansheet-Releases',
	DefaultDestinationAddress: '#Plansheet-Workspace',
	AutoRender: false,
	CSS: /*css*/`
		.ps-rel { padding: 20px 24px 60px; max-width: 1100px; }
		.ps-rel-head { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; }
		.ps-rel-head h1 { font-size: 22px; font-weight: 600; margin: 0; flex: 1; color: var(--theme-color-text-primary, #2c3140); }
		.ps-rel-btn { padding: 7px 14px; font-size: 13px; cursor: pointer; border: 1px solid var(--theme-color-border-default, #d8dde6);
			border-radius: 8px; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #2c3140); }
		.ps-rel-btn:hover { border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-rel-btn-primary { background: var(--theme-color-brand-primary, #3b6fd4); color: #fff; border-color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-rel-btn-disabled { opacity: 0.5; cursor: not-allowed; }
		.ps-rel-btn-disabled:hover { border-color: var(--theme-color-border-default, #d8dde6); }
		/* The lane is busy. Reads as a live status, not as a disabled button: there is something to go look at. */
		.ps-rel-btn-inflight { border-color: #d99a2b; color: #b3781a;
			background: color-mix(in srgb, #d99a2b 12%, transparent); }
		.ps-rel-btn-inflight:hover { border-color: #b3781a; }
		.ps-rel-err { background: var(--theme-color-status-error, #cc3333); color: #fff; padding: 8px 12px; border-radius: 8px; margin-bottom: 14px; font-size: 13px; }
		.ps-rel-envs { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 22px; }
		.ps-rel-env { background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 12px; padding: 14px 16px; }
		.ps-rel-env-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-rel-env-commit { font-size: 20px; font-weight: 700; color: var(--theme-color-text-primary, #2c3140); line-height: 1.2; margin-top: 3px;
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-rel-env-when { font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); margin-top: 1px; }
		.ps-rel-promote { display: flex; align-items: center; gap: 12px; background: var(--theme-color-background-panel, #fff);
			border: 1px solid var(--theme-color-border-light, #e7ecf0); border-radius: 12px; padding: 12px 16px; margin-bottom: 22px; }
		.ps-rel-promote-note { flex: 1; font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-rel-section { margin-bottom: 24px; }
		.ps-rel-section-title { font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: var(--theme-color-text-secondary, #5b6376); margin: 0 0 8px; }
		.ps-rel-empty { color: var(--theme-color-text-secondary, #8a93a6); font-style: italic; font-size: 13px; padding: 8px 2px; }
		.ps-rel-row { display: flex; gap: 10px; align-items: baseline; padding: 8px 12px; border: 1px solid var(--theme-color-border-light, #e7ecf0);
			border-radius: 8px; margin-bottom: 7px; background: var(--theme-color-background-panel, #fff); }
		.ps-rel-num { flex: 0 0 auto; font-weight: 700; font-size: 12px; color: var(--theme-color-text-secondary, #8a93a6); min-width: 34px; }
		/* The "WI-080" link that replaces the bare As-Built number (WI #452): the mark itself (.ps-mark-solo,
		   from Markup-Templates) already reads as a designator, so this only needs to say "clickable". */
		.ps-rel-num-link { flex: 0 0 auto; min-width: 34px; text-decoration: none; cursor: pointer; }
		.ps-rel-num-link:hover .ps-mark-solo { text-decoration: underline dotted; text-underline-offset: 2px; }
		.ps-rel-title { flex: 1; min-width: 0; font-size: 13px; color: var(--theme-color-text-primary, #2c3140); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
		.ps-rel-commit { flex: 0 0 auto; font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-rel-when { flex: 0 0 auto; font-size: 11px; color: var(--theme-color-text-secondary, #8a93a6); }
		.ps-rel-badge { flex: 0 0 auto; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.03em; }
		.ps-rel-badge.built { background: color-mix(in srgb, #8a93a6 16%, transparent); color: #5b6376; }
		.ps-rel-badge.deployed { background: color-mix(in srgb, var(--theme-color-brand-primary, #3b6fd4) 15%, transparent); color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-rel-badge.running { background: color-mix(in srgb, #d99a2b 18%, transparent); color: #b3781a; }
		.ps-rel-badge.validated { background: color-mix(in srgb, var(--theme-color-status-success, #3fae5a) 16%, transparent); color: #2f8a46; }
		.ps-rel-badge.promoted { background: color-mix(in srgb, #7b5cd6 16%, transparent); color: #6a48c8; }
		/* Delivered: finished work, but not by this pipeline. Teal keeps it plainly distinct from the
		   green of validated and the purple of promoted, both of which mean "this pipeline shipped it". */
		.ps-rel-badge.delivered { background: color-mix(in srgb, #2f8f8f 16%, transparent); color: #237070; }
		.ps-rel-workspace { flex: 0 0 auto; font-size: 11px; font-weight: 600; color: var(--theme-color-text-secondary, #5b6376);
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-rel-badge.failed { background: color-mix(in srgb, var(--theme-color-status-error, #cc3333) 15%, transparent); color: var(--theme-color-status-error, #cc3333); }
		.ps-rel-badge.muted { background: color-mix(in srgb, #8a93a6 12%, transparent); color: #8a93a6; }
		.ps-rel-validate { flex: 0 0 auto; padding: 4px 10px; font-size: 12px; cursor: pointer; border: 1px solid var(--theme-color-brand-primary, #3b6fd4);
			border-radius: 6px; background: transparent; color: var(--theme-color-brand-primary, #3b6fd4); }
		.ps-rel-validate:hover { background: color-mix(in srgb, var(--theme-color-brand-primary, #3b6fd4) 10%, transparent); }
		.ps-rel-row-built { flex-wrap: wrap; }
		.ps-rel-stranded { flex: 0 0 100%; font-size: 12px; line-height: 1.4; margin-top: 5px;
			color: var(--theme-color-text-secondary, #8a93a6); }
		.ps-rel-supersede { flex: 0 0 auto; font-size: 12px; padding: 3px 9px; cursor: pointer;
			border: 1px solid var(--theme-color-border-default, #d8dde6); border-radius: 6px; background: transparent;
			color: var(--theme-color-text-secondary, #5b6376); }
		.ps-rel-supersede:hover { border-color: var(--theme-color-status-error, #cc3333); color: var(--theme-color-status-error, #cc3333); }
		/* The node strip. Deliberately plain: it answers one question (is anything polling) and must not
		   compete with the environment cards for the eye. */
		.ps-rel-nodes { background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-light, #e7ecf0);
			border-radius: 12px; padding: 12px 16px; margin-bottom: 22px; }
		.ps-rel-nodes-note { font-size: 13px; color: var(--theme-color-text-secondary, #5b6376); }
		.ps-rel-node { display: flex; gap: 10px; align-items: baseline; font-size: 12px; margin-top: 7px; }
		.ps-rel-node-host { font-weight: 600; color: var(--theme-color-text-primary, #2c3140);
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-rel-node-roles { color: var(--theme-color-text-secondary, #8a93a6); flex: 1; min-width: 0; }
		.ps-rel-node-seen { color: var(--theme-color-text-secondary, #8a93a6); }
		/* The live line under a deploy request: what it last got through and how long it has been at it. */
		.ps-rel-timing { flex: 0 0 100%; font-size: 12px; line-height: 1.4; margin-top: 4px;
			color: var(--theme-color-text-secondary, #8a93a6); font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
		.ps-rel-durations { font-size: 12px; color: var(--theme-color-text-secondary, #8a93a6); margin: -4px 0 8px; }
		.ps-rel-log { white-space: pre-wrap; word-break: break-word; max-height: 60vh; overflow: auto; margin: 0;
			font-size: 12px; line-height: 1.45; color: var(--theme-color-text-primary, #2c3140);
			font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
			background: var(--theme-color-background-secondary, #f6f8fa); border: 1px solid var(--theme-color-border-light, #e7ecf0);
			border-radius: 8px; padding: 10px 12px; }
		@media (max-width: 820px) { .ps-rel-envs { grid-template-columns: 1fr; } }
	` + libMarkup.MarkupCSS,
	Templates:
	[
		...libMarkup.MarkupTemplates,
		{
			Hash: 'Plansheet-Releases',
			Template: /*html*/`
<div class="ps-rel">
	<div class="ps-rel-head">
		<h1>Releases</h1>
		{~TS:Releases-DeployDevReady:AppData.Releases.DeployDevReady~}
		{~TS:Releases-DeployDevInFlight:AppData.Releases.DeployDevInFlight~}
		<button class="ps-rel-btn" onclick="_Pict.views['Plansheet-Releases'].refresh()">Refresh</button>
	</div>
	{~TS:Releases-LoadError:AppData.Releases.LoadErrorSlot~}

	<div class="ps-rel-envs">{~TS:Releases-EnvCard:AppData.Releases.EnvCards~}</div>

	{~TS:Releases-Fleet:AppData.Releases.FleetSlot~}

	<div class="ps-rel-promote">
		<span class="ps-rel-promote-note">{~D:AppData.Releases.PromoteNote~}</span>
		{~TS:Releases-PromoteReady:AppData.Releases.PromoteReady~}
		{~TS:Releases-PromoteBlocked:AppData.Releases.PromoteBlocked~}
		{~TS:Releases-PromoteInFlight:AppData.Releases.PromoteInFlight~}
	</div>

	<div class="ps-rel-section">
		<p class="ps-rel-section-title">On dev, awaiting validation</p>
		{~TS:Releases-PendingEmpty:AppData.Releases.PendingEmpty~}
		{~TS:Releases-PendingRow:AppData.Releases.PendingRows~}
	</div>

	<div class="ps-rel-section">
		<p class="ps-rel-section-title">Validated, ready to promote</p>
		{~TS:Releases-ValidatedEmpty:AppData.Releases.ValidatedEmpty~}
		{~TS:Releases-Row:AppData.Releases.ValidatedRows~}
	</div>

	<div class="ps-rel-section">
		<p class="ps-rel-section-title">Built, not yet on dev</p>
		{~TS:Releases-BuiltEmpty:AppData.Releases.BuiltEmpty~}
		{~TS:Releases-BuiltRow:AppData.Releases.BuiltRows~}
	</div>

	{~TS:Releases-Delivered:AppData.Releases.DeliveredSlot~}

	<div class="ps-rel-section">
		<p class="ps-rel-section-title">Recently promoted</p>
		{~TS:Releases-PromotedEmpty:AppData.Releases.PromotedEmpty~}
		{~TS:Releases-Row:AppData.Releases.PromotedRows~}
	</div>

	<div class="ps-rel-section" id="ps-rel-requests">
		<p class="ps-rel-section-title">Deploy requests</p>
		{~TS:Releases-Durations:AppData.Releases.DurationsSlot~}
		{~TS:Releases-RequestsEmpty:AppData.Releases.RequestsEmpty~}
		{~TS:Releases-RequestRow:AppData.Releases.RequestRows~}
	</div>
</div>`
		},
		{ Hash: 'Releases-LoadError', Template: /*html*/`<div class="ps-rel-err">Could not load releases: {~D:Record.Message~}</div>` },
		{ Hash: 'Releases-EnvCard', Template: /*html*/`<div class="ps-rel-env"><div class="ps-rel-env-label">{~D:Record.Label~}</div><div class="ps-rel-env-commit">{~D:Record.Commit~}</div><div class="ps-rel-env-when">{~D:Record.Ago~}</div></div>` },
		{ Hash: 'Releases-PromoteReady', Template: /*html*/`<button class="ps-rel-btn ps-rel-btn-primary" onclick="_Pict.PictApplication.promoteToProd()">Promote to prod</button>` },
		{ Hash: 'Releases-PromoteBlocked', Template: /*html*/`<button class="ps-rel-btn ps-rel-btn-disabled" disabled>Promote to prod</button>` },
		// The lane is busy. No Promote button at all, because a button that will be refused is what got a promote
		// queued twice in the first place. "Show it" jumps to the request in the feed below, which already carries
		// the Cancel control for a node that died holding the lane.
		{
			Hash: 'Releases-PromoteInFlight',
			Template: /*html*/`<button class="ps-rel-btn" onclick="_Pict.views['Plansheet-Releases'].showRequests()">Show it</button>{~TS:Releases-PromoteForce:Record.ForceSlot~}`
		},
		{ Hash: 'Releases-PromoteForce', Template: /*html*/`<button class="ps-rel-supersede" onclick="_Pict.PictApplication.promoteToProdAnyway()">Promote anyway</button>` },
		{ Hash: 'Releases-DeployDevReady', Template: /*html*/`<button class="ps-rel-btn" onclick="_Pict.PictApplication.deployDev()">Deploy dev</button>` },
		{
			Hash: 'Releases-DeployDevInFlight',
			Template: /*html*/`<button class="ps-rel-btn ps-rel-btn-inflight" title="One dev deploy at a time. This one is still going." onclick="_Pict.views['Plansheet-Releases'].showRequests()">{~D:Record.Label~}</button>`
		},
		// The number cell is one of two 0-or-1 slots (see _workItemLink): a link to the Work Item the
		// As-Built closed out, or -- for the older rows that predate that link -- the As-Built's own number,
		// exactly as it always rendered.
		{ Hash: 'Releases-NumLink', Template: /*html*/`<a class="ps-rel-num-link" href="#/work-item/{~D:Record.IDWorkItem~}" data-idworkitem="{~D:Record.IDWorkItem~}" title="Open">{~TS:Markup-Designator:Record.MarkSlot~}</a>` },
		{ Hash: 'Releases-NumPlain', Template: /*html*/`<span class="ps-rel-num">#{~D:Record.Number~}</span>` },
		{ Hash: 'Releases-Row', Template: /*html*/`<div class="ps-rel-row">{~TS:Releases-NumLink:Record.NumLinkSlot~}{~TS:Releases-NumPlain:Record.NumPlainSlot~}<span class="ps-rel-title">{~D:Record.Title~}</span><span class="ps-rel-commit">{~D:Record.Commit~}</span><span class="ps-rel-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span></div>` },
		// Delivered elsewhere. The whole section is one slot, so a project with a single repository never
		// sees a heading that would only ever be empty. Each row names its workspace, because "delivered"
		// with no repository named is the same missing answer this work item is about.
		{
			Hash: 'Releases-Delivered',
			Template: /*html*/`
	<div class="ps-rel-section">
		<p class="ps-rel-section-title">Delivered elsewhere, not on this pipeline</p>
		{~TS:Releases-DeliveredRow:AppData.Releases.DeliveredRows~}
	</div>`
		},
		{ Hash: 'Releases-DeliveredRow', Template: /*html*/`<div class="ps-rel-row">{~TS:Releases-NumLink:Record.NumLinkSlot~}{~TS:Releases-NumPlain:Record.NumPlainSlot~}<span class="ps-rel-title">{~D:Record.Title~}</span><span class="ps-rel-workspace">{~D:Record.Workspace~}</span><span class="ps-rel-commit">{~D:Record.Commit~}</span><span class="ps-rel-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span></div>` },
		{ Hash: 'Releases-PendingRow', Template: /*html*/`<div class="ps-rel-row">{~TS:Releases-NumLink:Record.NumLinkSlot~}{~TS:Releases-NumPlain:Record.NumPlainSlot~}<span class="ps-rel-title">{~D:Record.Title~}</span><span class="ps-rel-commit">{~D:Record.Commit~}</span><span class="ps-rel-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span><button class="ps-rel-validate" onclick="_Pict.PictApplication.validateAsBuilt('AsBuilt', {~D:Record.IDAsBuilt~})">Validate</button></div>` },
		{ Hash: 'Releases-RequestRow', Template: /*html*/`<div class="ps-rel-row ps-rel-row-built"><span class="ps-rel-num">#{~D:Record.Number~}</span><span class="ps-rel-title">{~D:Record.Action~} &middot; {~D:Record.Environment~}</span><span class="ps-rel-commit">{~D:Record.Commit~}</span><span class="ps-rel-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span><span class="ps-rel-when">{~D:Record.Ago~}</span>{~TS:Releases-Log:Record.LogSlot~}{~TS:Releases-Cancel:Record.CancelSlot~}{~TS:Releases-Timing:Record.TimingSlot~}{~TS:Releases-Stalled:Record.StalledSlot~}</div>` },
		// A built row can say why it is stuck and offer the way out. Both parts are slots, so an unstuck row
		// renders exactly what it always did.
		{ Hash: 'Releases-BuiltRow', Template: /*html*/`<div class="ps-rel-row ps-rel-row-built">{~TS:Releases-NumLink:Record.NumLinkSlot~}{~TS:Releases-NumPlain:Record.NumPlainSlot~}<span class="ps-rel-title">{~D:Record.Title~}</span><span class="ps-rel-commit">{~D:Record.Commit~}</span><span class="ps-rel-badge {~D:Record.StatusClass~}">{~D:Record.Status~}</span>{~TS:Releases-Rebind:Record.RebindSlot~}{~TS:Releases-Supersede:Record.SupersedeSlot~}{~TS:Releases-Stranded:Record.StrandedSlot~}</div>` },
		// Node liveness. The badge carries the state and the sentence carries what to do about it; both come
		// from the heartbeat, so neither is inferred from how much work happens to be queued.
		{
			Hash: 'Releases-Fleet',
			Template: /*html*/`
	<div class="ps-rel-nodes">
		<span class="ps-rel-badge {~D:Record.StateClass~}">{~D:Record.State~}</span>
		<span class="ps-rel-nodes-note">{~D:Record.Note~}</span>
		{~TS:Releases-Node:AppData.Releases.NodeRows~}
	</div>`
		},
		{
			Hash: 'Releases-Node',
			Template: /*html*/`<div class="ps-rel-node"><span class="ps-rel-node-host">{~D:Record.Host~}</span><span class="ps-rel-node-roles">{~D:Record.Roles~}</span><span class="ps-rel-node-seen">{~D:Record.Seen~}</span><span class="ps-rel-badge {~D:Record.StateClass~}">{~D:Record.State~}</span></div>`
		},
		{ Hash: 'Releases-Stalled', Template: /*html*/`<span class="ps-rel-stranded">{~D:Record.Note~}</span>` },
		{ Hash: 'Releases-Durations', Template: /*html*/`<p class="ps-rel-durations">{~D:Record.Note~}</p>` },
		{ Hash: 'Releases-Timing', Template: /*html*/`<span class="ps-rel-timing">{~D:Record.Timing~}</span>` },
		{ Hash: 'Releases-Log', Template: /*html*/`<button class="ps-rel-supersede" onclick="_Pict.views['Plansheet-Releases'].showLog({~D:Record.IDDeployRequest~})">Log</button>` },
		{ Hash: 'Releases-Stranded', Template: /*html*/`<span class="ps-rel-stranded">{~D:Record.Note~}</span>` },
		{ Hash: 'Releases-Supersede', Template: /*html*/`<button class="ps-rel-supersede" onclick="_Pict.PictApplication.supersedeAsBuilt({~D:Record.IDAsBuilt~})">Supersede</button>` },
		// Rebind reads as the constructive action it is, not as the retirement next to it.
		{ Hash: 'Releases-Rebind', Template: /*html*/`<button class="ps-rel-validate" onclick="_Pict.PictApplication.rebindAsBuilt({~D:Record.IDAsBuilt~})">Rebind</button>` },
		{ Hash: 'Releases-Cancel', Template: /*html*/`<button class="ps-rel-supersede" onclick="_Pict.PictApplication.cancelDeployRequest({~D:Record.IDDeployRequest~})">Cancel</button>` },
		{ Hash: 'Releases-PendingEmpty', Template: /*html*/`<p class="ps-rel-empty">Nothing is on dev waiting to validate.</p>` },
		{ Hash: 'Releases-ValidatedEmpty', Template: /*html*/`<p class="ps-rel-empty">Nothing validated and waiting.</p>` },
		{ Hash: 'Releases-BuiltEmpty', Template: /*html*/`<p class="ps-rel-empty">Nothing built and waiting for a dev deploy.</p>` },
		{ Hash: 'Releases-PromotedEmpty', Template: /*html*/`<p class="ps-rel-empty">Nothing promoted yet.</p>` },
		{ Hash: 'Releases-RequestsEmpty', Template: /*html*/`<p class="ps-rel-empty">No deploy requests yet.</p>` }
	],
	Renderables:
	[
		{
			RenderableHash: 'Plansheet-Releases',
			TemplateHash: 'Plansheet-Releases',
			ContentDestinationAddress: '#Plansheet-Workspace',
			RenderMethod: 'replace'
		}
	]
};
